import * as Sentry from "@sentry/react";
import packageInfo from "../../package.json";

type FrontendLogLevel = "debug" | "info" | "warn" | "error";
type FrontendLogContext = Record<string, unknown>;

const environment = import.meta.env.VITE_ENV ?? "unknown";
// Only approved operational fields and primitive values are emitted; arbitrary
// objects can contain request payloads or personal data.
const safeContextKeys = new Set([
  "component",
  "operation",
  "outcome",
  "error_code",
  "error_type",
  "event_id",
  "http_method",
  "http_status",
  "duration_ms",
  "route",
]);
const sensitiveAssignment =
  /(["']?\b(?:authorization|access[_-]?token|refresh[_-]?token|id[_-]?token|token|password|secret|(?:api|private|secret|access)[_-]?key|otp|verification[_-]?code|cookie|headers?|e-?mail|phone(?:[_-]?number)?|mobile(?:[_-]?number)?)\b["']?\s*[:=]\s*)("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s,;&}]+)/gi;
const bearerToken = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const jwt = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const emailAddress = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

export const sanitizeLogText = (value: string): string =>
  value
    .replace(bearerToken, "$1 [REDACTED]")
    .replace(
      sensitiveAssignment,
      (_match, prefix: string, rawValue: string) => {
        const quote =
          rawValue[0] === '"' || rawValue[0] === "'" ? rawValue[0] : "";
        return `${prefix}${quote}[REDACTED]${quote}`;
      },
    )
    .replace(jwt, "[REDACTED_JWT]")
    .replace(emailAddress, "[REDACTED_EMAIL]")
    .replace(
      /([?&#](?:authorization|access[_-]?token|refresh[_-]?token|id[_-]?token|token|code|otp|password|secret|(?:api|private|secret|access)[_-]?key|email|phone|mobile)=)[^&#\s]*/gi,
      "$1[REDACTED]",
    )
    .slice(0, 8192);

const sanitizeRoute = (value: string): string => {
  let path = value.split(/[?#]/, 1)[0];
  try {
    path = new URL(value, window.location.origin).pathname;
  } catch {
    // Keep the safe path portion for relative or malformed URLs.
  }
  return path
    .replace(/\/(?:ACC-\d+|[0-9a-f]{8}-[0-9a-f-]{27,})\b/gi, "/:id")
    .replace(/\/[^/]*@[^/]+/g, "/:id");
};

const sanitizeContext = (context: FrontendLogContext) => {
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(context)) {
    if (
      safeContextKeys.has(key) &&
      value !== null &&
      value !== undefined &&
      typeof value === "string"
    ) {
      safe[key] =
        key === "route" ? sanitizeRoute(value) : sanitizeLogText(value);
    } else if (safeContextKeys.has(key) && typeof value === "boolean") {
      safe[key] = value;
    } else if (
      safeContextKeys.has(key) &&
      typeof value === "number" &&
      Number.isFinite(value)
    ) {
      safe[key] = value;
    }
  }
  return safe;
};

const writeLog = (
  level: FrontendLogLevel,
  message: string,
  context: FrontendLogContext = {},
) => {
  if (environment === "main" && (level === "debug" || level === "info")) {
    return;
  }
  const entry = {
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    logger: "incomlend.frontend",
    service: "incomlend-frontend",
    environment,
    version: packageInfo.version,
    message: sanitizeLogText(message),
    context: sanitizeContext(context),
  };
  console[level](JSON.stringify(entry));
};

export const frontendLogger = {
  debug: (message: string, context?: FrontendLogContext) =>
    writeLog("debug", message, context),
  info: (message: string, context?: FrontendLogContext) =>
    writeLog("info", message, context),
  warn: (message: string, context?: FrontendLogContext) =>
    writeLog("warn", message, context),
  error: (message: string, context?: FrontendLogContext) =>
    writeLog("error", message, context),
  apiFailure: (context: FrontendLogContext) => {
    const safeContext = sanitizeContext({
      component: "api",
      operation: "request",
      outcome: "failed",
      ...context,
    });
    writeLog("error", "API request failed", safeContext);
    Sentry.captureMessage("Frontend API request failed", {
      level: "error",
      tags: {
        component: "api",
        http_status: String(safeContext.http_status ?? "network"),
      },
      extra: safeContext,
    });
  },
};
